import React, { useRef, useEffect, useState } from "react";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

const StagingTable = ({ visible = false, setVisible, maxEntries = 10, entryDict = {}, apiProp, ref, validateRow }) => {
	if (!visible) return null;

	const keys = Object.keys(entryDict);
	const rowsRef = useRef([]);
	const [message, setMessage] = useState("");

	// Clears all rows and removes error highlights
	const clearRows = (rowsRef) => {
		rowsRef.current.forEach(row => {
			row.forEach(el => {
				if (el.type === "text" || el.type === "number") el.value = "";
				el.classList.remove("border-red-500");
			});
		});
	};

	// Close staging table
	const handleCancel = () => setVisible(false);

	// Auto-hide success message after 3s
	useEffect(() => {
		if (!message) return;
		const timer = setTimeout(() => setMessage(""), 3000);
		return () => clearTimeout(timer);
	}, [message]);

    const gridTemplate = `20px 160px repeat(${keys.length - 1}, 1fr)`;
	const getCellClass = (key) => "w-full border px-1 py-1 rounded text-xs";

	// Submit rows to backend
	const handleSubmit = async () => {
		const batchData = [];
        let hasError = false;
        let allErrors = "";
        
		rowsRef.current.forEach((rowEls, rowIndex) => {
			const obj = {};
			let filledCount = 0;
			let emptyCount = 0;

            keys.forEach((key, idx) => {
            	const el = rowEls[idx];
            	const val = el.value.trim();
            	obj[key] = val;
            	
            	// select stuffs
            	if (key === "advance") return;

            
            	// For select, treat default placeholder (like "") as empty
            	if (el.tagName.toLowerCase() !== "select") {
            		if (!val) emptyCount++;
            		else filledCount++;
            	}
            });


			// Skip fully empty rows
			if (filledCount === 0) {
				rowEls.forEach(el => el.classList.remove("border-red-500"));
				return;
			}

			// Partially filled → highlight empty cells
			if (filledCount > 0 && emptyCount > 0) {
				rowEls.forEach(el => {
					if (!el.value.trim()) {
    					el.classList.add("border-red-500");
					}
					else el.classList.remove("border-red-500");
				});
		    	hasError = true;
				allErrors += `Row ${rowIndex + 1}: Please fill all cells in this row or remove this row completely.\n`;

				
				return; // do not include partial rows
			}

			// Validate fully filled row if validateRow is provided
			if (validateRow) {
				const error = validateRow(obj);
				if (error) {
                    hasError = true;
                    allErrors += `Row ${rowIndex + 1}: ${error}\n`;
					rowEls.forEach(el => el.classList.add("border-red-500"));
					return;
				}
			}

			// Row is valid → push to batch
			rowEls.forEach(el => el.classList.remove("border-red-500"));
			batchData.push(obj);
		});
		
		if (hasError) {
    		// time to prompt all errors
            alert(allErrors);    		
        	return;
		}
		

		if (batchData.length === 0) {
			if (!hasError) alert("No valid rows to submit!");
			return;
		}

		if (!window.confirm(`Submit ${batchData.length} rows?`)) return;

		try {
			const res = await fetch(apiProp, {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(batchData),
			});

			if (!res.ok) {
				const data = await res.json();
				alert("Batch insert failed: " + (data.details || res.statusText));
				return;
			}

			setMessage(`Batch of ${batchData.length} rows submitted successfully!`);
			clearRows(rowsRef);
		} catch (err) {
			console.error(err);
			alert("Network error: " + err.message);
		}
	};

	return (
		<div ref={ref} className="mt-8 p-4 border rounded shadow overflow-x-auto">
			<div className="min-w-max">
				{/* Header */}
				<div className="grid gap-2 mb-2 font-bold" style={{ gridTemplateColumns: gridTemplate }}>
					<div className="text-center">#</div>
					{keys.map((key) => (
						<div key={key} className="text-center">{entryDict[key].label}</div>
					))}
				</div>

				{/* Data rows */}
				{Array.from({ length: maxEntries }).map((_, rowIndex) => (
					<div
						key={rowIndex}
						className="grid gap-2 mb-2 items-center"
						style={{ gridTemplateColumns: gridTemplate }}
						ref={(el) => {
							if (el) rowsRef.current[rowIndex] = Array.from(el.querySelectorAll("input, select"));
						}}
					>
						<div className="text-center">{rowIndex + 1}</div>
						{keys.map((key) => {
							const { type, options } = entryDict[key];
							const cellClass = getCellClass(key);

							return (
								<div key={key}>
									{type === "input" && (
										<input
											type={entryDict[key].additionalTypes || "text"}
											placeholder={entryDict[key].label}
											className={cellClass}
											defaultValue={entryDict[key].defaultValue ?? ""}

										/>
									)}
									{type === "select" && options && (
										<select className={cellClass}>
											{options.map((opt, i) => (
												<option key={i} value={opt.value ?? opt}>
													{opt.label ?? opt}
												</option>
											))}
										</select>
									)}
								</div>
							);
						})}
					</div>
				))}
			</div>

			{/* Actions */}
			<div className="flex justify-center gap-5 mt-5">
				<button className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 cursor-pointer" onClick={handleCancel}>Close</button>
				<button className="px-4 py-2 rounded-md border hover:bg-gray-100 cursor-pointer" onClick={() => clearRows(rowsRef)}>Clear</button>
				<button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer" onClick={handleSubmit}>Submit</button>
			</div>

			<p className="pt-4 font-bold">You can submit a partial table but with fully filled rows.</p>

			{message && (
				<div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
					<div className="bg-purple-600 text-white font-bold px-6 py-4 rounded-xl shadow-2xl text-lg animate-pulse opacity-95 pointer-events-auto">
						{message}
					</div>
				</div>
			)}
		</div>
	);
};

export default StagingTable;
